import apiClient from '@/lib/api-client';
import i18n from '@/i18n/config';

/**
 * The shapes of the person-week as the portal serves them. `Persoonsweek` is the colleague week
 * (`GET /api/v1/work-quality/employees/:userId`); `GET /me` is the narrow `EigenPersoonsweek` (story 19).
 *
 * Copied from two contracts that already exist and are not restated here: dqs-service
 * (`docs/api-contracts.md`, section Werkkwaliteit) owns every figure, and the portal proxy
 * (`apps/api/src/modules/work-quality/work-quality.service.ts`, `COLLEAGUE_WEEK` and `OWN_WEEK`) adds
 * `naam` beside every person number on the colleague week only. Nothing in this file computes: it
 * types what arrives and hands it on.
 */

/**
 * De redenen die dit antwoord op de plek van een getal kan zetten.
 *
 * Eén union voor de hele weergave, zodat een reden niet als losse `string` door de componenten
 * reist: een typfout in een reden zou dan stil het label van een andere toestand krijgen.
 */
export type Reden =
  | 'geen_beoordeeld_werk'
  | 'te_weinig_gegevens'
  | 'in_bespreking'
  | 'niet_gemeten_in_deze_meetversie';

/**
 * Het hoofdcijfer van één persoon (of het team) in één werksoort en één week — "in één keer goed"
 * (SPEC-werkkwaliteit-herontwerp, contract.md "Gedeelde vormen", M1).
 *
 * Een cijfer, of een reden en géén getal. Het percentage is een heel getal dat de dienst al afrondde;
 * dit scherm rekent het niet uit. De proxy laat binnen een Weekcijfer alleen deze vijf namen door.
 */
export type WeekcijferReden =
  | 'te_weinig_gegevens'
  | 'geen_werk_in_werksoort'
  | 'niet_nagekeken'
  | 'nog_niet_gemeten'
  | 'meting_mislukt'
  | 'niet_gemeten_in_deze_meetversie';

export type Weekcijfer =
  | { weekKey: string; aantalInEenKeerGoed: number; aantalBeoordeeld: number; percentage: number; reden?: never }
  | {
      weekKey: string;
      /** Alleen bij `te_weinig_gegevens`: hoeveel er wél beoordeeld is, onder de grens (M3). */
      aantalBeoordeeld?: number;
      reden: WeekcijferReden;
      aantalInEenKeerGoed?: never;
      percentage?: never;
    };

/** Deze week tegenover de vorige, in hele procentpunten — door de dienst vergeleken (M6). */
export type Richting =
  | { richting: 'hoger' | 'lager'; verschilPunten: number }
  | { richting: 'gelijk' | 'geen_vergelijking'; verschilPunten?: never };

/** `Richting` als losse, optionele velden: alleen aanwezig waar er een cijfer is. */
export interface RichtingVelden {
  richting?: Richting['richting'];
  verschilPunten?: number;
}

export interface Periode {
  weekKey: string;
  since: string;
  until: string;
}

export interface Volume {
  /** Absent on a week measured before the coverage version — never nought (AD-19). */
  aantalBeoordeeldeProducten?: number;
  aantalDoorEenAnderBeoordeeld: number;
}

export interface Dekking {
  aantalZelfGevalideerd?: number;
  aantalBeoordeeld?: number;
  aandeel?: number;
  reden?: Extract<Reden, 'geen_beoordeeld_werk' | 'niet_gemeten_in_deze_meetversie'>;
}

/**
 * De twee getoonde soorten, en de derde die het scherm niet toont.
 *
 * `beideLeeg` staat er sinds 4 september 2026 niet meer in — niet omdat het scherm hem niet toonde,
 * maar omdat de dienst hem niet meer meestuurt: hij wordt per paar geteld en nooit per veld, dus hij
 * is niet tot de getoonde velden te versmallen, en een vierde getal op een andere schaal binnen één
 * object is de vergissing van de volgende lezer (`docs/api-contracts.md`, sectie Werkkwaliteit).
 */
export interface Splitsing {
  leegAangevuld: number;
  waardeGewijzigd: number;
  hallucinatie?: number;
}

export interface Beoordelaarsrij {
  validator: number;
  /** Joined on by the portal; an unknown number keeps its row and its own wording. */
  naam: string;
  aantalProducten: number;
  mediaan?: number;
  gemiddelde?: number;
  percentageMetCorrectie?: number;
  /**
   * De twee getoonde soorten over de **getoonde** velden, sinds 4 september 2026 (AD-4).
   *
   * Zij ging over ál haar velden, en dan leverde "deze splitsing min de som van de getoonde
   * veldaantallen" precies het weggenomen totaal van haar gemaskeerde velden op. Nu telt de strook
   * op tot de veldenlijst eronder en valt er niets af te trekken — met als gevolg dat de twee
   * aantallen niet meer optellen tot het gemiddelde maal het aantal producten, en dat de strook dat
   * erbij hoort te zeggen.
   */
  splitsing?: Splitsing;
  /**
   * Waarom deze rij geen splitsing draagt, waar dat geen meting is (AD-19).
   *
   * `in_bespreking`: élk veld waarop deze beoordelaar corrigeerde staat in bespreking, dus er is
   * geen getoond veld om over op te tellen. `niet_gemeten_in_deze_meetversie`: een week van vóór
   * meetversie 8 draagt helemaal geen veldrijen. In beide gevallen blijven het aantal producten,
   * de mediaan, het gemiddelde en het afkeurpercentage staan — die gaan per product.
   */
  splitsingReden?: Extract<Reden, 'in_bespreking' | 'niet_gemeten_in_deze_meetversie'>;
  reden?: Extract<Reden, 'te_weinig_gegevens'>;
}

/**
 * Een veld dat pas vanaf een bepaalde meetversie bestaat: gemeten met een waarde, of uitdrukkelijk
 * niet gemeten (dqs-service `Meetversieveld`, AD-19). Nooit een lege waarde in plaats van "niet gemeten".
 */
export type Meetversieveld<T> =
  | { gemeten: true; waarde: T }
  | { gemeten: false; reden: Extract<Reden, 'niet_gemeten_in_deze_meetversie'> };

/** Waarom een voorbeeld wel of geen productlink krijgt (dqs-service `PRODUCTGRONDEN`, meetversie 11). */
export type Productgrond =
  | 'geen_gln'
  | 'geen_eenduidige_match'
  | 'niet_gevonden'
  | 'buiten_orderdienst'
  | 'geen_artikelhiërarchie'
  | 'gevonden';

/**
 * De link naar de controle, of de korte reden waarom er geen is. De dienst bouwt de link compleet
 * (SPEC-ordernummer-voorbeelden, besluit 20); de portal toont hem en stelt zelf niets samen.
 *
 * Beschrijvend, net als in `presentatie.ts` van dqs-service: `gevonden` kan óók met een reden komen
 * (geen linkbasis ingesteld), dus `grond` onderscheidt de twee vormen niet. `ExamplePair` leest het
 * antwoord daarom zelf na (`link` of `reden`) in plaats van op `grond` te vertrouwen.
 */
export type Productlink = { grond: 'gevonden'; link: string } | { grond: Productgrond; reden: string };

export interface Voorbeeld {
  productId: string;
  gtin: string;
  oudeWaarde: string;
  nieuweWaarde: string;
  validator: number;
  naam: string;
  datum: string;
  /*
   * Vanaf meetversie 11 (SPEC-feedback-productlink CAP-5). Optioneel: een antwoord van een oudere
   * dienst kent ze niet. Alleen `productlink` komt op het scherm; de andere vier worden getypt en
   * niet getoond.
   */
  productlink?: Meetversieveld<Productlink>;
  artikelHierarchieId?: Meetversieveld<string | null>;
  ordernummer?: Meetversieveld<string | null>;
  productStatus?: Meetversieveld<string | null>;
  orderService?: Meetversieveld<string | null>;
  /**
   * De naam van het product uit de nagekeken snapshot, vanaf meetversie 13 (contract §6, story 17).
   * Alleen deze naam komt op het scherm, vet boven oud → nieuw; geen GTIN of productnummer.
   */
  productnaam?: Meetversieveld<string | null>;
}

export interface Veldrij {
  veld: string;
  aantalProductenMetCorrectie?: number;
  aantalCorrecties?: number;
  splitsing?: Splitsing;
  /**
   * De breedte van het segment "leeg aangevuld" van de splitbalk, in procenten en in stappen van
   * vijf — de tekening zelf en geen aandeel (NFR9).
   *
   * Het scherm deelde hiervoor `leeg / (leeg + onjuist)`; sinds 4 september 2026 komt de breedte uit
   * het antwoord en rekent de browser niet meer. Hij **ontbreekt** waar geen van beide getoonde
   * soorten voorkomt: er valt dan geen balk te tekenen, en nul uit nul is een som die niemand maakte.
   */
  splitsingBreedteLeegAangevuld?: number;
  /**
   * Komen de twee getoonde soorten samen niet uit op `aantalCorrecties`?
   *
   * Het scherm telde daarvoor twee kolommen op. Dat oordeel komt sinds 4 september 2026 uit het
   * antwoord, zodat er ook hier niets wordt uitgerekend.
   *
   * **De dienst maakt hem verplicht op een rij die haar aantallen toont** (`presentatie.ts`,
   * `PersoonsVeldrij`); hier staat hij optioneel omdat dit portaal ook een antwoord moet kunnen
   * lezen dat van vóór 4 september 2026 komt. Ontbreekt hij, dan verschijnt de uitlegzin niet — het
   * scherm zegt dan niets in plaats van te beweren dat de twee soorten wél optellen, want dat zou
   * een uitspraak zijn die niemand heeft gedaan (AD-19).
   */
  splitsingTeltNietOp?: boolean;
  voorbeelden?: Voorbeeld[];
  reden?: Extract<Reden, 'te_weinig_gegevens' | 'in_bespreking'>;
}

/**
 * The two version numbers every measured answer carries (AD-24).
 *
 * Its own type because story 4.4 puts three answers in one view: the rule that two measurements are
 * never mixed has to be checkable across answer shapes that otherwise share nothing.
 */
export interface Meetversies {
  meetVersie: number;
  instellingVersie: number;
}

/**
 * What the accountability footer needs, and no more.
 *
 * `uitgeslotenVelden` is optional here on purpose: the person answer carries the list
 * (`presentatie.ts`, `bouwPersoonsantwoord`) and the overview answer does not
 * (`MedewerkerslijstAfbakening`). The footer therefore has to be able to say "this answer does not
 * name them" instead of "there are none" — those two are not the same sentence.
 */
export interface Verantwoording {
  weekKey: string;
  since: string;
  until: string;
  uitgeslotenVelden?: readonly string[];
  drempelProductenPerPersoon: number;
  /** Alleen bij meetversie 10 of later; de drempel van de hoofdsamenvatting. */
  drempelProductenPerVeld?: number;
  /**
   * Het geldende doel voor "in één keer goed", 1-100 (contract §1). De waarde van nu, ook bij een
   * oudere week. Optioneel: een dienst van vóór story 1 stuurt het niet.
   */
  doelInEenKeerGoed?: number;
}

export interface Afbakening extends Meetversies, Verantwoording {
  uitgeslotenVelden: string[];
}

/**
 * De Nederlandse (of Engelse) veldnaam per XML-pad — spec-veldnamen.
 *
 * Draagt alleen de paden die in dít antwoord voorkomen, en alleen die waarvoor een naam bestaat. Een
 * pad dat ontbreekt heeft geen naam: het scherm toont dan het pad, zoals voorheen. De kaart
 * ontbreekt helemaal wanneer de platformtabel niet te lezen was.
 */
export type Veldnamen = Record<string, string>;

/**
 * Het weekrapport per werksoort (`data.werksoorten`, dqs-service meetversie 10).
 *
 * `myxxtract` is vastleggen, `pca` is controles. Werk uit de twee staat nooit in één cijfer: er is een
 * blok per soort en geen totaal. Elk quotiënt komt klaar uit de dienst met zijn teller en noemer ernaast;
 * dit scherm deelt niets.
 */
export type WerksoortSleutel = 'myxxtract' | 'pca';

export interface Werkproductie {
  aantalProducten: number;
  aantalGewerkteDagen: number;
  /** Ontbreekt zonder gewerkte dagen; dan staat er `reden` en is een cijfer per dag een som die niemand maakte. */
  perGewerkteDag?: number;
  reden?: 'geen_gewerkte_dagen';
}

export interface ZonderAanpassing {
  aantal: number;
  aantalBeoordeeld: number;
  /** In procenten, op één decimaal. */
  aandeel: number;
}

/** Eén nakijker: hoeveel onderdelen (blokken) hij per product aanpaste, met teller en noemer. */
export interface Nakijkerrij {
  validator: number;
  /** Door het portaal erbij gezocht; een onbekend nummer houdt zijn rij. */
  naam: string;
  aantalProducten: number;
  aantalBlokken: number;
  gemiddeldeBlokkenPerProduct: number;
  mediaanBlokkenPerProduct: number;
  /*
   * Het deel met een correctie (contract §6, story 17), alleen naast een hoofdcijfer van de werksoort;
   * anders `metCorrectieReden` in plaats van de twee getallen. Alleen Details voor de leidinggevende
   * toont ze (M16). Optioneel: een dienst van vóór meetversie 13 kent ze niet.
   */
  aantalMetCorrectie?: number;
  /** Heel procent van `aantalProducten`, afgerond door de dienst; de browser rekent niet (AD-19). */
  aandeelMetCorrectie?: number;
  metCorrectieReden?: Extract<Reden, 'te_weinig_gegevens' | 'niet_gemeten_in_deze_meetversie'>;
}

/** De drie soorten aanpassing van de hoofdsamenvatting; `hallucinatie` is hier: weggehaald. */
export interface Foutensplitsing {
  leegAangevuld: number;
  waardeGewijzigd: number;
  hallucinatie: number;
}

/**
 * De uitleg per veld, zoals de wekelijkse batch die schrijft (ux-spec beslissing 7). Het portaal
 * maakt deze tekst niet zelf en roept geen taalmodel aan. Ontbreekt een tekst, dan toont de pagina
 * "uitleg volgt".
 */
export interface VeldUitleg {
  watGaatMis?: string;
  waarom?: string;
  zoMoetHet?: string;
}

export interface Hoofdveld {
  veld: string;
  blok: string;
  aantalProductenMetCorrectie: number;
  aantalCorrecties: number;
  splitsing: Foutensplitsing;
  /** Hoogstens drie, met de naam van de nakijker erbij. */
  voorbeelden: Voorbeeld[];
  reden?: never;
  /** Ontbreekt tot de batch uitleg levert; de pagina zegt dan "uitleg volgt". */
  uitleg?: VeldUitleg;
}

/** Een veld onder de drempel: alleen zijn naam en de reden, zonder aantallen en zonder voorbeelden. */
export interface GemaskeerdHoofdveld {
  veld: string;
  blok?: never;
  aantalProductenMetCorrectie?: never;
  splitsing?: never;
  voorbeelden?: never;
  /** `in_bespreking` sinds SPEC-meerweekenoverzicht CAP-8: geen aantal en geen voorbeeld op het scherm; alleen de naam, in Details onder "Wat niet meetelt" (story 11). */
  reden: Extract<Reden, 'te_weinig_gegevens' | 'in_bespreking'>;
}

/** Een rij van `alleVelden` (contract §6): een getoond veld zonder voorbeelden en zonder uitleg. */
export type AlleVeldenRij = Omit<Hoofdveld, 'voorbeelden' | 'uitleg'>;

/** Een veld van `alleVelden` onder de veldgrens: alleen zijn naam en de reden. */
export interface GemaskeerdAlleVeldenRij {
  veld: string;
  reden: Extract<Reden, 'te_weinig_gegevens'>;
}

export interface Hoofdsamenvatting {
  drempelProductenPerVeld: number;
  velden: Array<Hoofdveld | GemaskeerdHoofdveld>;
}

export interface GemetenWerksoort extends RichtingVelden {
  aantalBeoordeeldeProducten: number;
  productie: Werkproductie;
  zonderAanpassing: ZonderAanpassing;
  perNakijker: Nakijkerrij[];
  hoofdsamenvatting: Hoofdsamenvatting;
  /**
   * Elk veld met een correctie in deze werksoort, behalve velden in bespreking, in dienstvolgorde
   * (contract §6, story 17). Alleen Details voor de leidinggevende leest dit (M16). Optioneel: een
   * dienst van vóór meetversie 13 kent het niet; dan valt Details terug op `hoofdsamenvatting`.
   */
  alleVelden?: Array<AlleVeldenRij | GemaskeerdAlleVeldenRij>;
  /** Het hoofdcijfer van deze week (contract §2). Optioneel: een dienst van vóór meetversie 12 kent het niet. */
  inEenKeerGoed?: Weekcijfer;
  /** `aantalBeoordeeld − aantalInEenKeerGoed`, door de dienst uitgerekend. */
  aantalMetCorrectie?: number;
  /** Het hoofdcijfer van de week ervoor, met dezelfde regel. */
  vorigeWeek?: Weekcijfer;
  reden?: never;
}

/** Of een werksoort geen rapport heeft: geen beoordeeld werk, of een week van vóór meetversie 10. */
export interface OngemetenWerksoort {
  reden: Extract<Reden, 'geen_beoordeeld_werk' | 'niet_gemeten_in_deze_meetversie'>;
  /** Ook zonder rapport draagt de werksoort haar hoofdcijfer — als reden. */
  inEenKeerGoed?: Weekcijfer;
}

export type Werksoortrapport = GemetenWerksoort | OngemetenWerksoort;

export type Werksoorten = Partial<Record<WerksoortSleutel, Werksoortrapport>>;

export interface Persoonsweek {
  veldnamen?: Veldnamen;
  /** Ontbreekt bij een antwoord van een dienst van vóór meetversie 10. */
  werksoorten?: Werksoorten;
  userId: number;
  naam: string;
  /** Uit dezelfde opzoeking als `naam`; ontbreekt bij een onbekende of lege naam (story 8). */
  voornaam?: string;
  achternaam?: string;
  periode: Periode;
  volume: Volume;
  dekking: Dekking;
  beoordelaars: Beoordelaarsrij[];
  velden: Veldrij[];
  veldenReden?: Extract<Reden, 'niet_gemeten_in_deze_meetversie'>;
  reden?: Extract<Reden, 'geen_beoordeeld_werk' | 'te_weinig_gegevens'>;
  afbakening: Afbakening;
}

/**
 * The three shapes the module answers in, as `docs/auth-rbac-system.md` writes them out. A week
 * without a measurement is `data: null` with a reason — never an empty person-week, because an
 * empty week reads as "nothing was corrected in your work" (AD-19, AD-27).
 */
export type Antwoord<T> =
  | {
      data: T;
      reden?: never;
      laatsteGeslaagdeRun?: never;
      laatsteGeslaagdeWeek?: never;
      volgendeRun?: never;
      fout?: never;
      metingIngeschakeld?: never;
    }
  | {
      data: null;
      reden: string;
      laatsteGeslaagdeRun: string | null;
      /**
       * Wat de meetdienst over die afwezigheid meestuurt — story 4.6. Alle drie optioneel, en dat
       * is de hele afspraak: de dienst laat een veld weg wanneer zij het niet weet, in plaats van
       * er `null` of een lege tekst voor in de plaats te zetten. Een scherm dat een afwezigheid als
       * uitkomst toont is precies wat AD-19 en AD-27 verbieden.
       */
      laatsteGeslaagdeWeek?: string;
      volgendeRun?: string;
      fout?: string;
      /**
       * Staat de wekelijkse meting aan? De uitzondering op de regel hierboven: de dienst stuurt dit
       * veld altijd mee, omdat de stand van een schakelaar altijd bekend is.
       *
       * Hier blijft hij tóch optioneel, en dat gaat over deze kant van de lijn: een oudere meetdienst
       * of een proxy die de waarde niet kon lezen, stuurt hem niet. Dan is het "onbekend" en niet
       * "uit" — het scherm gedraagt zich dan als voorheen. Alleen een letterlijke `false` zegt dat er
       * geen rapport komt.
       */
      metingIngeschakeld?: boolean;
    };

export type PersoonsweekAntwoord = Antwoord<Persoonsweek>;

// ── Het eigen antwoord: alleen wat Mijn feedback leest (contract.md §6, story 19) ───────────────

/*
 * `/me` draagt sinds story 19 alleen de toelatingslijst "Alleen wat het scherm toont": geen namen, geen
 * nakijkers, geen persoonsbreed `velden`. Mijn feedback leest alleen deze typen; het volle antwoord van een
 * collega (`Persoonsweek`) past er structureel in, zodat het Zo-ziet-kader dezelfde componenten deelt.
 */

/** In `waarde` van `productlink` alleen `link` of `reden`; `grond` leest niemand. */
export type EigenProductlink = { link?: string; reden?: string };

export interface EigenVoorbeeld extends Pick<Voorbeeld, 'productId' | 'oudeWaarde' | 'nieuweWaarde' | 'productnaam'> {
  productlink?: Meetversieveld<EigenProductlink>;
}

export interface EigenHoofdveld extends Pick<Hoofdveld, 'veld' | 'aantalProductenMetCorrectie' | 'uitleg'> {
  /** Hoogstens drie; zonder naam van de nakijker. */
  voorbeelden?: EigenVoorbeeld[];
  reden?: never;
}

export type EigenGemaskeerdHoofdveld = Pick<GemaskeerdHoofdveld, 'veld' | 'reden'>;

export interface EigenGemetenWerksoort
  extends RichtingVelden,
    Pick<GemetenWerksoort, 'inEenKeerGoed' | 'aantalMetCorrectie' | 'vorigeWeek'> {
  hoofdsamenvatting: { velden: Array<EigenHoofdveld | EigenGemaskeerdHoofdveld> };
  reden?: never;
}

export type EigenWerksoortrapport = EigenGemetenWerksoort | OngemetenWerksoort;

export type EigenAfbakening = Pick<
  Afbakening,
  | 'weekKey'
  | 'since'
  | 'until'
  | 'uitgeslotenVelden'
  | 'drempelProductenPerPersoon'
  | 'drempelProductenPerVeld'
  | 'doelInEenKeerGoed'
  | 'meetVersie'
  | 'instellingVersie'
>;

export interface EigenPersoonsweek {
  /** De aanvrager zelf; de proxy toetst hem (AD-27). */
  userId: number;
  periode: Periode;
  afbakening: EigenAfbakening;
  /** Ontbreekt bij een antwoord van een dienst van vóór meetversie 10. */
  werksoorten?: Partial<Record<WerksoortSleutel, EigenWerksoortrapport>>;
  veldnamen?: Veldnamen;
}

export type EigenWeekAntwoord = Antwoord<EigenPersoonsweek>;

/**
 * De schermtaal als queryparameter — spec-veldnamen, AC6.
 *
 * De api zoekt de Nederlandse of Engelse veldnaam op in dezelfde platformtabel. De webapplicatie
 * zet geen `accept-language`, dus zonder deze parameter zou een Engelse gebruiker stilzwijgend
 * Nederlandse veldnamen krijgen.
 */
function taalparameter(): string {
  // `startsWith` en geen gelijkheid: `resolvedLanguage` kan `en-GB` zijn, en dat is Engels.
  // Dezelfde toets als `leesTaal` aan de api-kant, zodat de twee niet uit elkaar kunnen lopen.
  return `&language=${i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'nl'}`;
}

export async function getEigenWeek(week: string): Promise<EigenWeekAntwoord> {
  const response = await apiClient.get<EigenWeekAntwoord>(
    `/work-quality/me?week=${encodeURIComponent(week)}${taalparameter()}`,
  );
  return response.data;
}

/**
 * The colleague's week — story 4.4, AD-17.
 *
 * The full person week with names and the manager's details (`alleVelden`, the reviewers and their
 * share, contract.md §6); only `MedewerkerDetails` reads those. The own week is the narrow
 * `EigenPersoonsweek` (story 19); this answer fits it, so Mijn feedback and the Zo-ziet-kader share
 * one `Rapportkaart`.
 */
export async function getMedewerkerWeek(userId: number, week: string): Promise<PersoonsweekAntwoord> {
  const response = await apiClient.get<PersoonsweekAntwoord>(
    `/work-quality/employees/${encodeURIComponent(String(userId))}?week=${encodeURIComponent(week)}${taalparameter()}`,
  );
  return response.data;
}

// ── Overzicht over weken (SPEC-meerweekenoverzicht-feedback, contract.md) ───────────────────────

/**
 * Eén veld in een werksoortweek met cijfers. Een veld dat ontbreekt had 0 producten met een
 * correctie; een veld met een reden draagt geen getal. Een reden die het portaal niet kent, geldt als
 * geen cijfer (contract.md, Portalroutes).
 */
export type WekenoverzichtVeld =
  | { veld: string; aantalProductenMetCorrectie: number; reden?: never }
  | { veld: string; reden: Extract<Reden, 'te_weinig_gegevens' | 'in_bespreking'>; aantalProductenMetCorrectie?: never };

/**
 * Een werksoort in één week: precies één van de vier vormen van het contract, en sinds story 8 het
 * hoofdcijfer erbij (`inEenKeerGoed`), dat de Maandgrafiek en de Maandtabel lezen.
 */
export type WekenoverzichtWerksoort = (
  | { reden: Extract<Reden, 'niet_gemeten_in_deze_meetversie' | 'geen_beoordeeld_werk'> }
  | { aantalBeoordeeldeProducten: number; reden: Extract<Reden, 'te_weinig_gegevens'> }
  | { aantalBeoordeeldeProducten: number; aantalInEenKeerGoed: number; velden: WekenoverzichtVeld[]; reden?: never }
) & { inEenKeerGoed?: Weekcijfer };

/** Eén week van het overzicht: zonder document of mislukt alleen een reden, anders beide werksoorten. */
export type WekenoverzichtWeek =
  | { weekKey: string; reden: 'nog_niet_gemeten' | 'meting_mislukt' }
  | { weekKey: string; meetVersie: number; werksoorten: Record<WerksoortSleutel, WekenoverzichtWerksoort>; reden?: never };

export interface Wekenoverzicht {
  userId: number;
  van: string;
  tot: string;
  drempelProductenPerWerksoort: number;
  drempelProductenPerVeld: number;
  /** Het geldende doel, op het hoogste niveau: dit antwoord heeft geen `afbakening` (contract §1). */
  doelInEenKeerGoed?: number;
  /** Elke week van `van` tot en met `tot`, oudste eerst. */
  weken: WekenoverzichtWeek[];
}

/**
 * Het eigen overzicht over weken (`/me/weeks`, contract.md §6 "Alleen wat het scherm toont", story 19):
 * zonder `van`, `tot`, `doelInEenKeerGoed`, `meetVersie` en de losse `aantalInEenKeerGoed`. De trend, de
 * leerkaart en de tab Maand lezen alleen dit type; het volle overzicht van een collega past erin.
 */
export type EigenWekenoverzichtWerksoort = (
  | { reden: Extract<Reden, 'niet_gemeten_in_deze_meetversie' | 'geen_beoordeeld_werk'> }
  | { aantalBeoordeeldeProducten: number; reden: Extract<Reden, 'te_weinig_gegevens'> }
  | { aantalBeoordeeldeProducten: number; velden: WekenoverzichtVeld[]; reden?: never }
) & { inEenKeerGoed?: Weekcijfer };

export type EigenWekenoverzichtWeek =
  | { weekKey: string; reden: 'nog_niet_gemeten' | 'meting_mislukt' }
  | { weekKey: string; werksoorten: Record<WerksoortSleutel, EigenWekenoverzichtWerksoort>; reden?: never };

export interface EigenWekenoverzicht {
  userId: number;
  drempelProductenPerWerksoort: number;
  drempelProductenPerVeld: number;
  /** Elke gevraagde week, oudste eerst. */
  weken: EigenWekenoverzichtWeek[];
}

/** Het eigen overzicht over weken; de persoon komt uit de sessie, nooit uit het adres. */
export async function getEigenWeken(van: string, tot: string): Promise<EigenWekenoverzicht> {
  const response = await apiClient.get<{ data: EigenWekenoverzicht }>(
    `/work-quality/me/weeks?from=${encodeURIComponent(van)}&to=${encodeURIComponent(tot)}`,
  );
  return response.data.data;
}

/** Het overzicht over weken van één collega (alleen met het brede recht). */
export async function getMedewerkerWeken(userId: number, van: string, tot: string): Promise<Wekenoverzicht> {
  const response = await apiClient.get<{ data: Wekenoverzicht }>(
    `/work-quality/employees/${encodeURIComponent(String(userId))}/weeks?from=${encodeURIComponent(van)}&to=${encodeURIComponent(tot)}`,
  );
  return response.data.data;
}

// ── Alle medewerkers (story 4.4) ────────────────────────────────────────────────────────────────

/**
 * One row of the overview.
 *
 * **Counts and nothing else.** AD-17 writes the list out — "uitsluitend naam, aantal producten,
 * aandeel zelf afgetekend en het aantal beoordelaars — geen correctiegraad, geen mediaan, geen
 * percentage" — and the portal proxy enforces the same allow-list a second time
 * (`OVERVIEW_ROW_KEYS`). Typing anything else here would be the first step back towards the league
 * table this module exists to remove.
 */
export interface Medewerkersrij {
  userId: number;
  /** Joined on by the portal; an unknown number keeps its row and its own wording. */
  naam: string;
  /** Uit dezelfde opzoeking als `naam`; ontbreekt bij een onbekende of lege naam (story 8). */
  voornaam?: string;
  achternaam?: string;
  /**
   * Het hoofdcijfer per werksoort — AD-17 deels teruggedraaid op 31-10-2026 (contract §3). Een
   * werksoort die ontbreekt wordt niet verzonnen. De rijen staan op `userId`; nooit een volgorde op
   * cijfer.
   */
  werksoorten?: Partial<Record<WerksoortSleutel, OverzichtWerksoort>>;
  aantalProducten?: number;
  /**
   * How many of those products somebody else reviewed.
   *
   * This is the number "geen beoordeeld werk deze week" hangs on, and not the reason beside it: a
   * colleague who signed off her whole week herself carries that reason *with* her products — 38 of
   * them in the measured material — and a row that read the reason alone would say she did nothing.
   */
  aantalDoorEenAnderBeoordeeld?: number;
  zelfAfgetekend?: ZelfAfgetekend;
  aantalBeoordelaars?: number;
  reden?: Extract<Reden, 'geen_beoordeeld_werk' | 'te_weinig_gegevens' | 'niet_gemeten_in_deze_meetversie'>;
}

/** Eén werksoort van een overzichtsrij; elke laag laat de proxy alleen bij naam door. */
export interface OverzichtWerksoort extends RichtingVelden {
  cijfer: Weekcijfer;
  vorigeWeek: Weekcijfer;
  /** Precies vier weken, oudste eerst, eindigend op de gekozen week — of de reden dat dat niet lukte. */
  verloop: Weekcijfer[] | { reden: 'niet_te_laden' };
  groep: 'aandacht' | 'op_koers' | 'geen_cijfer';
  /** Alleen bij een cijfer: ligt het percentage onder het doel? */
  onderDoel?: boolean;
}

export interface AantalPerGroep {
  aandacht: number;
  op_koers: number;
  geen_cijfer: number;
}

/** Het team per werksoort: de som over wie een cijfer heeft (M9), of de reden dat die er niet is. */
export type TeamWerksoort =
  | ({ cijfer: Weekcijfer; vorigeWeek: Weekcijfer; aantalPerGroep: AantalPerGroep; reden?: never } & Richting)
  | { reden: 'niemand_met_cijfer' | 'niets_nagekeken'; aantalPerGroep: { aandacht: 0; op_koers: 0; geen_cijfer: number } };

export type ZelfAfgetekendWerksoort =
  | { aantal: number; aantalProducten: number; percentage: number; reden?: never }
  | { reden: 'geen_werk' | 'niet_gemeten_in_deze_meetversie' };

export interface ZelfAfgetekend {
  aantal?: number;
  aandeel?: number;
  reden?: Extract<Reden, 'geen_beoordeeld_werk' | 'niet_gemeten_in_deze_meetversie'>;
}

export interface Weekdekking {
  aantalZelfGevalideerd?: number;
  aantalBeoordeeld?: number;
  aandeel?: number;
  reden?: Extract<Reden, 'geen_beoordeeld_werk' | 'niet_gemeten_in_deze_meetversie'>;
}

export interface OverzichtDekking {
  week: Weekdekking;
  aantalMedewerkers?: number;
  aantalZonderBeoordeeldWerk?: number;
  aantalOnderDrempel?: number;
  aantalMetCijfers?: number;
  aantalBuitenDePopulatie?: number;
}

/** The reasons the passed-through field list of the weekly report can carry instead of rows. */
export type TopVeldenReden =
  | 'geen_weekrapport'
  | 'weekrapport_niet_leesbaar'
  | 'geen_topvelden_in_weekrapport'
  | 'topvelden_onbruikbaar';

export interface TopVeld {
  veld: string;
  categorie?: string;
  aas?: number;
  total?: number;
  gemist?: number;
  onjuist?: number;
  hallucinatie?: number;
  fieldOcc?: number;
}

/**
 * The field list of the weekly AI report, passed through and not measured here.
 *
 * It counts over the AI fields of that report and therefore over a smaller set of fields than the
 * rest of this module, which measures over every corrected field. `bereikAantalAiVelden` says how
 * large that set is, and the screen has to say it out loud: the two counts may never be laid side
 * by side.
 */
export interface TopVelden {
  venster?: string;
  berekenDatum?: string;
  bereikAantalAiVelden?: number;
  velden?: TopVeld[];
  reden?: TopVeldenReden;
}

export interface OverzichtAfbakening extends Meetversies, Verantwoording {
  drempelProductenPerPersoon: number;
}

export interface Medewerkersoverzicht {
  veldnamen?: Veldnamen;
  medewerkers: Medewerkersrij[];
  /** Team leest uitsluitend dit blok (contract §3). Zonder persoon. */
  team?: Partial<Record<WerksoortSleutel, TeamWerksoort>>;
  zelfAfgetekendPerWerksoort?: Partial<Record<WerksoortSleutel, ZelfAfgetekendWerksoort>>;
  dekking: OverzichtDekking;
  topVelden?: TopVelden;
  afbakening: OverzichtAfbakening;
}

export interface Signaleringsbeoordelaar {
  validator: number;
  naam: string;
  aantalVergelekenProducten?: number;
  aantalProductenMetCorrectie?: number;
  percentageProductenMetCorrectie?: number;
}

export interface Signaleringspaar {
  beoordelaars: Signaleringsbeoordelaar[];
  factor?: number;
}

export interface Signalering {
  veld: string;
  paren?: Signaleringspaar[];
}

export interface SignaleringenAfbakening extends Meetversies {
  weekKey: string;
  since: string;
  until: string;
  signaleringsfactor: number;
  minimumBeoordeeldeProducten: number;
}

export interface Signaleringenantwoord {
  veldnamen?: Veldnamen;
  signaleringen: Signalering[];
  reden?: Extract<Reden, 'niet_gemeten_in_deze_meetversie'>;
  afbakening: SignaleringenAfbakening;
}

export interface WeekrapportAfbakening extends Meetversies {
  weekKey: string;
  since: string;
  until: string;
}

/**
 * Eén van de vier kerngetallen van het weekrapport — spec-weekrapport-dashboard.
 *
 * Afwezigheid is hier een waarde. Een cijfer dat de meetdienst niet kon meten draagt géén `waarde`,
 * en nooit een nul: een nul op het scherm leest als een meting. De rij zelf blijft wél staan, zodat
 * het scherm kan zeggen *dat* er niets gemeten is in plaats van stilzwijgend een vak te laten vallen.
 *
 * `verschilPp` komt uit de dienst en wordt hier niet uitgerekend (AD-19/NFR9).
 */
export interface Kerngetal {
  sleutel: 'nauwkeurigheid' | 'volledigheid' | 'precisie' | 'hallucinatie';
  /** De waarde in de periode van het rapport, als fractie. Afwezig als er niets gemeten is. */
  waarde?: number;
  vorige?: number;
  /** Het verschil in procentpunten, door de dienst berekend. */
  verschilPp?: number;
  /** Of een stijging goed nieuws is. `false` bij hallucinatie, en alleen daar. */
  hogerIsBeter: boolean;
}

/**
 * Eén van de negen categorieën, als staaf — spec-weekrapport-dashboard, tweede helft.
 *
 * `staafBreedte` is de breedte die getekend wordt en geen verhouding die het scherm uitrekent.
 * Tailwind kan een `w-[${n}%]` niet bouwen, dus een balk kiest een letterlijke klasse uit
 * `WIDTH_CLASSES`, en dat kiezen is afronden — precies de som die AD-19/NFR9 uit de browser houdt.
 * Het precedent is `splitsingBreedteLeegAangevuld`.
 */
export interface Categoriestaaf {
  /** De naam zoals de meetdienst hem geeft, in het Nederlands. Het portaal vertaalt hem niet. */
  categorie: string;
  /** De AAS als percentage met één decimaal. Afwezig als er die week niets te meten viel. */
  waarde?: number;
  vorige?: number;
  /** Het verschil in procentpunten, door de dienst berekend. */
  verschilPp?: number;
  /** Een veelvoud van vijf, 0 t/m 100. Afwezig als er geen waarde is. */
  staafBreedte?: number;
  /**
   * Aantal velden dat goed was, optioneel. De meetdienst levert dit nog niet; het portaal toont
   * het alleen samen met `noemer`, en alleen als beide kloppen met `waarde`.
   */
  teller?: number;
  /** Aantal velden in de categorie, optioneel. Alleen tonen samen met `teller`. */
  noemer?: number;
}

/**
 * Eén rij die het rapport slechter noemde dan de vorige periode — tweede helft, AC5 t/m AC7.
 *
 * **`soort` bestaat omdat tweederde van een week helemaal geen achteruitgang hoeft te zijn.** De
 * drempel in de dienst is "AAS-daling > 2pp OF correcties > 20% gestegen", dus een rij kan hier
 * staan mét een gestegen AAS (Contactgegevens 58,1% → 69,4%). Eén rood blok zou die als alarm tonen.
 *
 * Een veldrij draagt `veld` — de sleutel waarop dit portaal veldpaden vertaalt — en een categorierij
 * draagt `categorie`. Nooit allebei, en nooit `naam`: dat is elders de sleutel voor persoonsnamen.
 */
export interface Regressierij {
  soort: 'aas_gedaald' | 'meer_correcties';
  categorie?: string;
  veld?: string;
  waarde?: number;
  vorige?: number;
  /** Positief betekent dat de AAS steeg; dat komt voor bij een `meer_correcties`-rij. */
  verschilPp?: number;
  correcties: number;
  correctiesVorige: number;
}

export interface Weekrapport {
  /**
   * De Nederlandse veldnamen bij de paden in dit antwoord — spec-veldnamen.
   *
   * Staat hier sinds de regressies veldpaden dragen. De proxy zet de kaart er vanzelf op zodra er
   * een `veld`-sleutel in het antwoord zit, waar die ook zit.
   */
  veldnamen?: Veldnamen;
  berekenDatum?: string;
  venster?: string;
  /** The stored text, byte for byte. The screen shows it and rewrites nothing. */
  rapportMarkdown?: string;
  /**
   * De vier kerngetallen — spec-weekrapport-dashboard.
   *
   * **Afwezig** wanneer de meetdienst ze niet stuurt (een oudere versie). Dat is iets anders dan een
   * lege lijst, en het scherm behandelt die twee ook anders: bij afwezigheid staat de rapporttekst
   * uitgeklapt, want dan is die alles wat er is.
   */
  kerngetallen?: Kerngetal[];
  /**
   * De negen categorieën als staven.
   *
   * **Afwezig** bij een oudere meetdienst; een lege lijst zou betekenen dat het rapport er geen
   * had. Het scherm behandelt die twee verschillend.
   */
  categorieen?: Categoriestaaf[];
  /**
   * De regressierijen. Een lege lijst is hier een echt antwoord — "deze week geen regressies" is
   * iets wat het rapport zegt — en iets anders dan afwezigheid.
   */
  regressies?: Regressierij[];
  reden?: 'geen_weekrapport';
  afbakening: WeekrapportAfbakening;
}

export type MedewerkersAntwoord = Antwoord<Medewerkersoverzicht>;
export type SignaleringenAntwoord = Antwoord<Signaleringenantwoord>;
export type WeekrapportAntwoord = Antwoord<Weekrapport>;

export async function getMedewerkers(week: string): Promise<MedewerkersAntwoord> {
  const response = await apiClient.get<MedewerkersAntwoord>(
    `/work-quality/employees?week=${encodeURIComponent(week)}${taalparameter()}`,
  );
  return response.data;
}

export async function getSignaleringen(week: string): Promise<SignaleringenAntwoord> {
  const response = await apiClient.get<SignaleringenAntwoord>(
    `/work-quality/signals?week=${encodeURIComponent(week)}${taalparameter()}`,
  );
  return response.data;
}

export async function getWeekrapport(week: string): Promise<WeekrapportAntwoord> {
  const response = await apiClient.get<WeekrapportAntwoord>(
    // De taal hoort er sinds het regressieblok veldpaden toont: zonder haar zoekt de api
    // stilzwijgend de Nederlandse naam op, ook in een Engels scherm.
    `/work-quality/week-report?week=${encodeURIComponent(week)}${taalparameter()}`,
  );
  return response.data;
}

// ── Instellingen (story 4.5) ────────────────────────────────────────────────────────────────────

/**
 * De drempel die nu geldt, en de versie waarin zij is vastgelegd.
 *
 * Geen `Antwoord<…>`: deze twee routes kennen geen ongemeten vorm. Het contract geeft `data: null`
 * met een reden aan de zes routes die een gemeten week lézen en uitdrukkelijk niet aan deze — een
 * lezing zonder instellingendocument komt terug met de standaardwaarde en versie 0. De proxy weigert
 * een `null` hier dan ook met een storing (`SETTINGS`, `unmeasuredAllowed: false`): een beheerder
 * die te horen krijgt dat haar wijziging is opgeslagen terwijl er niets is weggeschreven, is de ene
 * faalwijze die deze route niet mag hebben.
 */
export interface Instellingen {
  drempelProductenPerPersoon: number;
  /** Het doel voor "in één keer goed", 1-100. De proxy weigert een antwoord zonder (story 8). */
  doelInEenKeerGoed: number;
  /** Het bereik dat de dienst voor de drempel aanneemt (besluit 31-10-2026 #3): 20 en 100. */
  drempelMinimum?: number;
  drempelMaximum?: number;
  instellingVersie: number;
}

/** Wat een opslag verandert: de drempel, het doel of allebei — minstens één. */
export type InstellingenWijziging =
  | { drempelProductenPerPersoon: number; doelInEenKeerGoed?: number }
  | { drempelProductenPerPersoon?: number; doelInEenKeerGoed: number };

export interface InstellingenAntwoord {
  data: Instellingen;
}

/**
 * De dunste meting die bij een drempel nog een cijfer krijgt.
 *
 * Krijgt niemand er een, dan staat er een reden en géén `aantalProducten: 0` — nul producten zou
 * lezen als een meting die op niets rust, terwijl er helemaal geen meting getoond wordt.
 */
export interface DunsteGetoondeMeting {
  aantalProducten?: number;
  reden?: 'geen_getoonde_meting';
}

/**
 * De afbakening van een wat-als, en zij draagt met opzet géén `instellingVersie` (AD-24).
 *
 * De wat-als slaat niets op en telt niet als meting. Een pagina die er wél een kreeg zou dit
 * antwoord niet kunnen onderscheiden van een lezing van de instelling die werkelijk geldt, en zou
 * precies de vermenging bevestigen die dat versienummer bestaat om te voorkomen. De dienst laat de
 * sleutel weg en de portaalproxy haalt hem er nog een tweede keer af (`WHAT_IF`).
 */
export interface WatAlsAfbakening {
  weekKey: string;
  since: string;
  until: string;
  meetVersie: number;
}

/**
 * Wat een drempelwaarde met één gemeten week zou doen, zonder iets op te slaan.
 *
 * Elke telling is optioneel: een week die deze doorsnede niet kent, laat het getal wég in plaats van
 * er een nul voor in de plaats te zetten. Het scherm telt ze nooit bij elkaar op — de vier
 * deeltellingen hoeven niet op te tellen tot `aantalMedewerkers`, en een som zou een getal op het
 * scherm zetten dat niemand heeft gemeten (NFR9).
 */
export interface WatAls {
  drempel: number;
  aantalMedewerkers?: number;
  aantalZonderBeoordeeldWerk?: number;
  aantalOnderDrempel?: number;
  aantalMetCijfers?: number;
  aantalBuitenDePopulatie?: number;
  dunsteGetoondeMeting?: DunsteGetoondeMeting;
  /** Story 8: samen, voor "5 van de 14 cijfers". */
  aantalCijfers?: number;
  /** Story 8: per werksoort wie onder de drempel zakt, met naam zoals in het overzicht. */
  perWerksoort?: Partial<
    Record<WerksoortSleutel, { aantalCijfers: number; onderDrempel: Array<WatAlsPersoon & { aantalBeoordeeld: number }> }>
  >;
  afbakening: WatAlsAfbakening;
}

/** Een persoon in een wat-als: het nummer, en de naam die de proxy erbij zocht. */
export interface WatAlsPersoon {
  userId: number;
  naam: string;
  voornaam?: string;
  achternaam?: string;
}

/** Wat een doel met één gemeten week zou doen: wie met dit doel in groep Aandacht valt (contract §4). */
export interface WatAlsDoel {
  perWerksoort: Partial<
    Record<WerksoortSleutel, { aantalMetCijfer: number; aantalOnderDoel: number; aandacht: WatAlsPersoon[] }>
  >;
}

export type WatAlsAntwoord = Antwoord<WatAls>;
export type WatAlsDoelAntwoord = Antwoord<WatAlsDoel>;

export async function getInstellingen(): Promise<InstellingenAntwoord> {
  const response = await apiClient.get<InstellingenAntwoord>('/work-quality/settings');
  return response.data;
}

/**
 * De drempel, het doel of allebei verzetten. De enige schrijfactie van deze module.
 *
 * Alleen de velden die er staan gaan de deur uit, en als getal en nooit als tekst: `"25"` wordt door
 * beide grenzen geweigerd en niet omgerekend, en een portaal dat hier alvast omrekent zou een
 * verzoek versturen dat de tweede grens afkeurt — de beheerder hoort dan een afwijzing over een
 * waarde die zij niet typte.
 */
export async function putInstellingen(wijziging: InstellingenWijziging): Promise<InstellingenAntwoord> {
  const response = await apiClient.put<InstellingenAntwoord>('/work-quality/settings', wijziging);
  return response.data;
}

/**
 * De wat-als voor een drempel (`?drempel=`) of voor een doel (`?doel=`) — nooit allebei.
 *
 * De drempelvorm staat als laatste: `vi.mocked` leest de laatste overload, en de bestaande toetsen
 * bootsen de drempel na.
 */
export function getWatAls(week: string, vraag: { doel: number }): Promise<WatAlsDoelAntwoord>;
export function getWatAls(week: string, vraag: { drempel: number }): Promise<WatAlsAntwoord>;
export async function getWatAls(
  week: string,
  vraag: { drempel: number } | { doel: number },
): Promise<WatAlsAntwoord | WatAlsDoelAntwoord> {
  const [naam, waarde] = 'doel' in vraag ? ['doel', vraag.doel] : ['drempel', vraag.drempel];
  const response = await apiClient.get<WatAlsAntwoord | WatAlsDoelAntwoord>(
    `/work-quality/settings/what-if?week=${encodeURIComponent(week)}&${naam}=${encodeURIComponent(String(waarde))}`,
  );
  return response.data;
}
